import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  UserPlus,
  ArrowRightLeft,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import {
  useLeads,
  useCreateLead,
  useUpdateLead,
  useDeleteLead,
  useConvertLead,
  useScoreLead,
  useAssignLead,
  type LeadFilters,
  type Lead,
  type LeadStatus,
  type LeadSource,
} from '../hooks/useLeads';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface LeadsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<LeadStatus, { label: string; color: string }> = {
  new: { label: 'New', color: 'blue' },
  contacted: { label: 'Contacted', color: 'orange' },
  qualified: { label: 'Qualified', color: 'green' },
  unqualified: { label: 'Unqualified', color: 'gray' },
  converted: { label: 'Converted', color: 'purple' },
};

const sourceLabels: Record<LeadSource, string> = {
  web_form: 'Web Form',
  referral: 'Referral',
  cold_call: 'Cold Call',
  email: 'Email',
  event: 'Event',
  partner: 'Partner',
  other: 'Other',
};

const statusTabs: { label: string; value: LeadStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Qualified', value: 'qualified' },
];

function getScoreColor(score: number): string {
  if (score >= 80) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  if (score >= 25) return 'bg-orange-500';
  return 'bg-red-500';
}

export function LeadsView({ onNavigate }: LeadsViewProps) {
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    source: 'web_form' as LeadSource,
    notes: '',
  });

  const filters: LeadFilters = {
    status: statusFilter || undefined,
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useLeads(filters);
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();
  const convertLead = useConvertLead();
  const scoreLead = useScoreLead();
  const assignLead = useAssignLead();

  const leads = data?.leads ?? [];

  function handleCreate() {
    createLead.mutate(formData, {
      onSuccess: () => {
        setShowCreateForm(false);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', source: 'web_form', notes: '' });
      },
    });
  }

  function handleConvert(lead: Lead) {
    convertLead.mutate({
      id: lead.id,
      data: { createContact: true, createOrganization: !!lead.company, createDeal: true },
    });
  }

  function handleScore(lead: Lead) {
    scoreLead.mutate(lead.id);
  }

  function handleDelete(lead: Lead) {
    deleteLead.mutate(lead.id);
    if (selectedLead?.id === lead.id) setSelectedLead(null);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Leads</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} lead${data.total !== 1 ? 's' : ''}` : 'Manage your leads'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateForm(true)}>
          <Plus className="h-4 w-4" />
          New Lead
        </button>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          {/* Status tabs */}
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  statusFilter === tab.value
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search leads..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Main content */}
        <div className={cn('flex-1 p-6', selectedLead && 'pr-0')}>
          {error ? (
            <div className="card p-8 text-center text-sm text-red-600">
              Failed to load leads. Please try again.
            </div>
          ) : isLoading ? (
            <div className="card">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="h-4 w-32 animate-pulse rounded bg-surface-100" />
                  <div className="flex-1" />
                  <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
                </div>
              ))}
            </div>
          ) : leads.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Score</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Source</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Company</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assignee</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last Activity</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {leads.map((lead) => {
                    const sCfg = statusLabels[lead.status];
                    return (
                      <tr
                        key={lead.id}
                        className={cn(
                          'transition-colors hover:bg-surface-50 cursor-pointer',
                          selectedLead?.id === lead.id && 'bg-brand-50',
                        )}
                        onClick={() => setSelectedLead(lead)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">
                            {lead.firstName} {lead.lastName}
                          </p>
                          {lead.email && <p className="text-xs text-slate-400">{lead.email}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {lead.score !== undefined ? (
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-16 rounded-full bg-surface-200">
                                <div
                                  className={cn('h-2 rounded-full', getScoreColor(lead.score))}
                                  style={{ width: `${Math.min(lead.score, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-slate-600">{lead.score}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">--</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge color={sCfg.color} size="sm">{sCfg.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {sourceLabels[lead.source] ?? lead.source}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{lead.company ?? '--'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{lead.assigneeName ?? '--'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {lead.lastActivityAt ? new Date(lead.lastActivityAt).toLocaleDateString() : '--'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            {lead.status !== 'converted' && (
                              <button
                                onClick={() => handleConvert(lead)}
                                disabled={convertLead.isPending}
                                className="btn-secondary btn-sm"
                                title="Convert to contact + deal"
                              >
                                <ArrowRightLeft className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleScore(lead)}
                              disabled={scoreLead.isPending}
                              className="btn-secondary btn-sm"
                              title="Score lead"
                            >
                              <Star className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={UserPlus}
              title="No leads yet"
              description="Create your first lead to start tracking prospects."
              actionLabel="Add Lead"
              onAction={() => setShowCreateForm(true)}
            />
          )}
        </div>

        {/* Detail panel */}
        {selectedLead && (
          <div className="w-96 border-l border-surface-200 bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                {selectedLead.firstName} {selectedLead.lastName}
              </h2>
              <button onClick={() => setSelectedLead(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              {selectedLead.email && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Email</p>
                  <p className="text-sm text-slate-900">{selectedLead.email}</p>
                </div>
              )}
              {selectedLead.phone && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Phone</p>
                  <p className="text-sm text-slate-900">{selectedLead.phone}</p>
                </div>
              )}
              {selectedLead.company && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Company</p>
                  <p className="text-sm text-slate-900">{selectedLead.company}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-medium text-slate-500">Status</p>
                <Badge color={statusLabels[selectedLead.status].color} size="sm">
                  {statusLabels[selectedLead.status].label}
                </Badge>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Source</p>
                <p className="text-sm text-slate-900">{sourceLabels[selectedLead.source]}</p>
              </div>

              {/* Score breakdown */}
              {selectedLead.score !== undefined && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Score: {selectedLead.score}</p>
                  <div className="h-3 w-full rounded-full bg-surface-200">
                    <div
                      className={cn('h-3 rounded-full', getScoreColor(selectedLead.score))}
                      style={{ width: `${Math.min(selectedLead.score, 100)}%` }}
                    />
                  </div>
                  {selectedLead.scoreBreakdown && selectedLead.scoreBreakdown.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {selectedLead.scoreBreakdown.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">{s.signal}</span>
                          <span className="font-medium text-slate-900">+{s.points}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedLead.notes && (
                <div>
                  <p className="text-xs font-medium text-slate-500">Notes</p>
                  <p className="text-sm text-slate-600">{selectedLead.notes}</p>
                </div>
              )}

              <div className="pt-4 border-t border-surface-200 flex gap-2">
                {selectedLead.status !== 'converted' && (
                  <button
                    onClick={() => handleConvert(selectedLead)}
                    disabled={convertLead.isPending}
                    className="btn-primary btn-sm flex-1"
                  >
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                    Convert
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedLead)}
                  disabled={deleteLead.isPending}
                  className="btn-secondary btn-sm text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Lead</h2>
              <button onClick={() => setShowCreateForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">First Name</label>
                  <input className="input" value={formData.firstName} onChange={(e) => setFormData((f) => ({ ...f, firstName: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Last Name</label>
                  <input className="input" value={formData.lastName} onChange={(e) => setFormData((f) => ({ ...f, lastName: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label mb-1">Email</label>
                <input className="input" type="email" value={formData.email} onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Phone</label>
                <input className="input" value={formData.phone} onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Company</label>
                <input className="input" value={formData.company} onChange={(e) => setFormData((f) => ({ ...f, company: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Source</label>
                <select className="input" value={formData.source} onChange={(e) => setFormData((f) => ({ ...f, source: e.target.value as LeadSource }))}>
                  {Object.entries(sourceLabels).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label mb-1">Notes</label>
                <textarea className="input" rows={3} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createLead.isPending || !formData.firstName || !formData.lastName}>
                {createLead.isPending ? 'Creating...' : 'Create Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
